import { Button } from "@svea/design-system-components-react";
import { useEffect, useId, useState } from "react";
import { Field, type FieldInputProps, Form } from "react-final-form";
import { useText } from "../../../hooks";
import type {
	PepRelationAnswer,
	PepRelationQuestion,
	PepRelationValue,
} from "../../../types/question";
import { isRequired, stringToSlug, validatePastOrTodayDate } from "../../../utils";
import {
	Checkbox,
	Fieldset,
	FormControlLabel,
	FormHelperText,
	Legend,
	TextField,
} from "../../form-fields";
import { SmallCard } from "../../small-card";
import { Stack } from "../../stack";
import { Text } from "../../typography";
import { formatFieldName } from "../utils";

type Props = {
	question: PepRelationQuestion;
};

const onCheckboxChange = (
	{ onChange, value = [] }: FieldInputProps<Array<string>, HTMLElement>,
	checked: boolean,
	answerChoice: string,
) => {
	const values = checked ? [answerChoice, ...value] : value.filter((v) => v !== answerChoice);
	onChange(values);
};

type PersonProps = {
	name: string;
	birthDate: string;
	onDelete: () => void;
};

const PepPerson = ({ name, birthDate, onDelete }: PersonProps) => (
	<SmallCard className="bg-background-primary">
		<Stack direction="row" gap={2}>
			<Stack>
				<Text bold>{name}</Text>
				<Text>{birthDate}</Text>
			</Stack>
			<Button
				variant="icon"
				icon="trash"
				aria-label="Ta bort"
				onPress={() => {
					onDelete();
				}}
				data-testid="deletePep"
			/>
		</Stack>
	</SmallCard>
);

export const PepRelation = ({ question }: Props) => {
	const [t, texts] = useText();
	const describedById = useId();
	const [hasSubmitFailed, setHasSubmitError] = useState(false);
	const {
		question: questionText,
		questionId,
		questionTypeContext: { validPepReasons },
	} = question;

	return (
		<Fieldset
			aria-invalid={hasSubmitFailed}
			aria-describedby={hasSubmitFailed ? describedById : undefined}
		>
			<Field<Array<PepRelationAnswer>>
				validate={(value) =>
					value?.length > 0 ? undefined : texts.kyc.beneficialOwnerQuestions.atLeastOnePerson
				}
				name={formatFieldName(questionId)}
				format={(v) => v || []} // defaults the field to empty array instead of empty string
				render={({ input, meta }) => {
					const handleOnSubmit = (value: PepRelationValue) =>
						input.onChange([...(input?.value || []), { answerValue: value }]);

					return (
						<Stack gap={2}>
							<Form<PepRelationValue>
								onSubmit={handleOnSubmit}
								render={({ handleSubmit, form: innerForm }) => {
									return (
										<Stack gap={4} align="start">
											<Stack gap={2}>
												<Legend>{questionText}</Legend>
												<TextField
													label={t(texts.kyc.beneficialOwnerQuestions.name)}
													name="name"
													type="text"
													validators={[isRequired]}
												/>
												<TextField
													label={t(texts.kyc.beneficialOwnerQuestions.birthDate)}
													name="birthDate"
													type="text"
													validators={[isRequired, validatePastOrTodayDate]}
													placeholder="ÅÅÅÅ-MM-DD"
												/>
											</Stack>
											<Field<Array<string>>
												name="pepReasons"
												format={(v) => v || []}
												validate={(values = []) => isRequired(values)}
											>
												{({ input, meta }) => {
													// biome-ignore lint/correctness/useHookAtTopLevel: Not conditional
													useEffect(() => {
														setHasSubmitError(!!meta.submitFailed && !!meta.error);
													}, [meta]);
													return (
														<>
															{validPepReasons.map((reason) => (
																<FormControlLabel label={reason} key={reason} gap={2}>
																	<Checkbox
																		checked={input.value.some((v) => v === reason)}
																		name="pep-reason"
																		onChange={(e) => {
																			onCheckboxChange(input, !!e?.target.checked, reason);
																		}}
																		value={reason}
																	/>
																</FormControlLabel>
															))}
															<FormHelperText error id={describedById}>
																{meta.touched && meta.error && t(meta.error)}
															</FormHelperText>
														</>
													);
												}}
											</Field>

											<Button
												onPress={() => {
													handleSubmit();
													if (innerForm.getState().valid) innerForm.restart();
												}}
											>
												{t(texts.kyc.beneficialOwnerQuestions.addPerson)}
											</Button>
										</Stack>
									);
								}}
							/>

							{input.value.map(({ answerValue: { birthDate, name } }) => (
								<PepPerson
									key={stringToSlug(`${name}${birthDate}`)}
									birthDate={birthDate}
									name={name}
									onDelete={() => {
										input.onChange(
											input.value.filter(
												({ answerValue: { birthDate: existingBirthDate, name: existingName } }) =>
													existingName !== name || existingBirthDate !== birthDate,
											),
										);
									}}
								/>
							))}
							<FormHelperText error id={describedById}>
								{meta.touched && meta.error}
							</FormHelperText>
						</Stack>
					);
				}}
			/>
		</Fieldset>
	);
};
